import isHotkey from "is-hotkey";
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { BaseEditor, createEditor, Editor, Transforms, Text } from 'slate';
import { HistoryEditor } from 'slate-history';
import { Editable, ReactEditor, Slate, withReact } from "slate-react";
import { HOTKEYS, toggleMark } from "./rich-editor-helpers";
import { BlockButton, MarkButton, Toolbar } from "./rich-text-editor-header";
import { BoldIcon, EmojiEditorIcon, ItalicIcon, ListBulletedIcon, ListNumberedIcon, StrikeoutIcon, UnderlinedIcon } from "../../../icon";
import { Popover } from '@mui/material';
import Emojis from '../../conversations/emojis';

export type ParagraphElement = {
  type: 'paragraph'
  children: CustomText[]
}

export type BlockElement = {
  type: 'block-quote'
  children: CustomText[]
}

export type CustomElement = ParagraphElement | BlockElement

type CustomText = { text: string; bold?: true; italic?: true; code?: true }

declare module 'slate' {
  interface CustomTypes {
    Editor: BaseEditor & ReactEditor & HistoryEditor
    Element: CustomElement
    Text: CustomText
  }
}

interface Props {
  value: any;
  setValue: any;
  isRedonly?: boolean;
  name?: string;
  IsMobileDevice?: boolean;
  Whatsapptemplates?: boolean;
}

const Element = ({ attributes, children, element }: { attributes: any, children: any, element: any }) => {
  switch (element.type) {
    case "bulleted-list":
      return <ul {...attributes}>{children}</ul>;

    case "numbered-list":
      return <ol {...attributes}>{children}</ol>;

    case "list-item":
      return <li {...attributes}>{children}</li>;
    default:
      return <p {...attributes} className="m-0">{children}</p>;
  }
};

const Leaf = ({ attributes, children, leaf }: { attributes: any, children: any, leaf: any }) => {
  if (leaf.bold) {
    children = <b>{children}</b>
  }

  if (leaf.strikethrough) {
    children = <s>{children}</s>
  }

  if (leaf.italic) {
    children = <i>{children}</i>
  }

  if (leaf.underline) {
    children = <u>{children}</u>
  }

  return <span {...attributes}>{children}</span>
}


const RichEditor = (props: Props) => {
  const renderElement = useCallback((props) => <Element {...props} />, [props]);
  const renderLeaf = useCallback((props) => <Leaf {...props} />, [props]);
  const editor = useMemo(() => withReact(createEditor()), []);
  const [anchorEmoji, setAnchorEmoji] = useState<Element | null>(null)
  const emojiRef = React.createRef<HTMLDivElement>();


  const handleEmojiSelect = (e: any) => {
    let emoji = e.native;
    editor.insertText(emoji);
    setAnchorEmoji(null)
  };


  useEffect(() => {
    editor.children = props.value
  }, [props.value])

  const handleKeyDown = (event) => {
      const { selection } = editor;
      if (selection) {
        const [node] = Editor.node(editor, selection);
 
        if (Text.isText(node) && node.text && node.text.includes('{{') && node.text.includes('}}')) {
          reorderVariables(editor);
        }
      }
  };
 
  const reorderVariables = (editor) => {
    const { selection } = editor;
    let variableCounter = 1;
 
    for (const [node, path] of Array.from(Editor.nodes(editor, { at: [], match: (n) => Text.isText(n), }))) {
      const matches = node.text.match(/{{\d+}}/g);
 
      if (matches) {
        let updatedText = node.text;

        matches.forEach((match) => {
          const expectedVariable = `{{${variableCounter}}}`;
          if (match !== expectedVariable) {
            updatedText = updatedText.replace(match, expectedVariable);
          }
          variableCounter++;
        });
 
        if (updatedText !== node.text) {
          Transforms.insertText(editor, updatedText, { at: path });
        }
      }
    }
 
    // Restore the selection to its original position
    // if (selection) {
    //   console.log(selection)
    //   Transforms.select(editor, selection);
    // }
  };
 
  return (
    <>
      {!props.isRedonly ?
        <div className="p-1 br-2 b-around-text-field-border-4 d-flex flex-direction">
          <Slate editor={editor} initialValue={props.value} onChange={value => props.setValue(value)}>
            <div className="bb-primary-divider-4 o-hidden">
              <Toolbar IsMobileDevice={props.IsMobileDevice}>
                <div className="d-flex ml-auto">
                  <MarkButton format="bold" icon={BoldIcon}/>
                  <MarkButton format="italic" icon={ItalicIcon} />
                  <MarkButton format="strikethrough" icon={StrikeoutIcon} />
                  {!props.Whatsapptemplates && <>
                    <MarkButton format="underline" icon={UnderlinedIcon} />
                    <div className="border-left-4"></div>
                    <BlockButton format="bulleted-list" icon={ListNumberedIcon} />
                    <BlockButton format="numbered-list" icon={ListBulletedIcon} />
                    <div className="border-right-4"></div>
                  </>
                  }
                  <div ref={emojiRef}
                    onClick={() => {
                      if (anchorEmoji) {
                        setAnchorEmoji(null)

                      } else {
                        setAnchorEmoji(emojiRef.current)
                      }
                    }}>
                    <EmojiEditorIcon className={`c-pointer`} />
                  </div>
                </div>
              </Toolbar>
            </div>
            <div className="h-v-12 o-y-scroll o-x-hidden">
              <Editable
                name={props.name}
                renderElement={renderElement}
                renderLeaf={renderLeaf}
                placeholder="Description"
                spellCheck
                autoFocus
                onKeyDown={event => {
                  for (const hotkey in HOTKEYS) {
                    if (isHotkey(hotkey, event as any)) {
                      event.preventDefault()
                      const mark = HOTKEYS[hotkey]
                      toggleMark(editor, mark)
                    }
                  }
                  handleKeyDown(event)
                }}
                className={"h4-regular color-text-field-font"}
              />
            </div>
            {Boolean(anchorEmoji) &&
              <Popover
                open={Boolean(anchorEmoji)}
                anchorEl={anchorEmoji}
                onClose={() => setAnchorEmoji(null)}
                anchorOrigin={{
                  vertical: 'top',
                  horizontal: 'left',
                }}
                transformOrigin={{
                  vertical: 'bottom',
                  horizontal: 'right',
                }}
              >
                <Emojis
                  handleEmojiSelect={handleEmojiSelect}
                />
              </Popover>
            }
          </Slate>
        </div>
        :
        <>
          <Slate editor={editor} initialValue={props.value} onChange={() => { }}>
            <Editable
              renderElement={renderElement}
              renderLeaf={renderLeaf}
              readOnly
              placeholder=""
              className={"h5-regular color-alert-title"}
            />
          </Slate>
        </>
      }
    </>
  );
};

export default RichEditor;